import React, { useState, useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import getValue from "lodash/get";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import { withRedux } from "hoc";
import SearchLayout from "layouts/Search";
import SearchDetailLayout from "layouts/SearchDetails";
import { resetSiteSearch, showResults } from "./FindSite/provider/actions";
import { resetCountrySiteSearch } from "./CountrySite/provider/actions";
import CountHead from "./FindSite/Results/countText.js";
import SiteSearch from "./FindSite/Search/SiteSearch";
import CountrySearch from "./CountrySite/Search/CountrySearch";
import SiteSearchDetails from "./FindSite/Results/SearchDetails";
import CountryDetails from "./CountrySite/Results/CountryDetails";
import ReferralForm from "./ReferralForm";

const defaultValue = 50;
const ENTIRE_WORLD_RADIUS = 11000;

const FindSite = (props) => {
  const {
    hasSearched,
    setHasSearched,
    headingMessage,
    options,
    siteCount,
    resetCountrySearch,
    resetSiteSearch,
    isEmbed = false,
    onSiteConnectClick = null,
    useBrowserLocation = false,
    initialLocation,
    showAllSites,
    canViewByCountry = false,
    onTrialSitesChange = null,
    recap_key = "",
    website_language = "",
    website_country = "",
    google_place_api_key = "",
    referral_comment_box_setting = false,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const {
    SiteSearch: SiteSearchOverrideProps,
    SearchDetails: SearchDetailsOverrideProps,
    CountrySearch: CountrySearchOverrideProps,
    CountryDetails: CountryDetailsOverrideProps,
  } = theme.props || {};
  const { siteSearchLinkText = "Find a site", ...otherSiteSearchOverideProps } =
    SiteSearchOverrideProps || {};
  const {
    headingText = "Results",
    resultsCountText,
    mapViewText = "Map view",
    listMapViewText = "List/Map view",
    listViewText = "List view",
    ...otherSearchDetailsOverrideProps
  } = SearchDetailsOverrideProps || {};
  const {
    countrySitesHeadingText = "View sites by country",
    countryLinkText = "View by country",
    ...otherCountrySearchOverideProps
  } = CountrySearchOverrideProps || {};
  const [searchData, setSearchData] = useState(
    initialLocation
      ? {
          lat: initialLocation.lat,
          lng: initialLocation.lng,
          radius: initialLocation.radius,
          country: initialLocation.country,
          place: initialLocation.place,
        }
      : {
          lat: 0,
          lng: 0,
          radius: showAllSites ? ENTIRE_WORLD_RADIUS : defaultValue,
          country: null,
        }
  );
  // const [hasSearched, setHasSearched] = useState(false);
  const [viewMode, setViewMode] = useState("both");
  const [nextViewMode, setNextViewMode] = useState("map");
  const [showByCountry, setShowByCountry] = useState(false);
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [referralSiteDetails, setReferralSiteDetails] = useState({});
  const locRef = useRef();

  React.useEffect(() => {
    if (isMobile) {
      setViewMode("map");
      setNextViewMode("list");
    } else {
      setViewMode("both");
      setNextViewMode("map");
    }
  }, [isMobile]);

  function sendMessage(data) {
    window.parent.postMessage(data, "*");
  }

  function calculateHeight() {
    let $body = document.getElementsByTagName("body");
    sendMessage({ height: $body[0].scrollHeight });
  }

  React.useEffect(() => {
    if (window.parent) {
      setTimeout(calculateHeight, 500);
    }
  }, []);

  const viewModeTextMapper = React.useMemo(() => {
    return {
      both: listMapViewText,
      map: mapViewText,
      list: listViewText,
    };
  }, [listMapViewText, mapViewText, listViewText]);

  const handleLinkClick = useCallback(
    (e) => {
      e.preventDefault();
      if (showByCountry) {
        resetCountrySearch();
      } else {
        resetSiteSearch();
        setHasSearched(false);
        // setViewMode("both")
      }
      setShowByCountry(!showByCountry);
    },
    [
      showByCountry,
      setShowByCountry,
      resetCountrySearch,
      resetSiteSearch,
      setHasSearched,
    ]
  );
  const handleViewModeChange = useCallback(
    (e) => {
      e.preventDefault();
      setNextViewMode(viewMode);
      if (!isMobile) {
        if (viewMode === "both" && hasSearched) {
          setViewMode("map");
        }
        if (viewMode === "map" && hasSearched) {
          setViewMode("both");
        }
      } else {
        if (viewMode === "map" && hasSearched) {
          setViewMode("list");
        }
        if (viewMode === "list" && hasSearched) {
          setViewMode("map");
        }
      }
    },
    [viewMode, hasSearched, isMobile]
  );

  const handleListModeListItemClick = () => {
    if (viewMode === "list" && hasSearched) {
      setViewMode("map");
    }
  };

  const handleReferralFormCancel = () => {
    setShowReferralForm(false);
    document.getElementsByTagName("body")[0]?.scrollIntoView();
    setTimeout(calculateHeight, 200);
  };
  window.addEventListener("message", (e) => {
    if (e.data.hasOwnProperty("siteLocator:connect")) {
      setReferralSiteDetails(e.data);
      setShowReferralForm(true);
    }
  });

  return (
    <>
      <>
        <div ref={locRef} style={showReferralForm ? { display: "none" } : {}}>
          <SearchLayout
            headingCountry={`<h6>${countrySitesHeadingText}</h6>`}
            headingSite={headingMessage} // two different heading to remove same Xpath jquery problem on theme variable edit
            editable={!showByCountry}
            linkLabel={showByCountry ? siteSearchLinkText : countryLinkText}
            onLinkClick={handleLinkClick}
            layout={showByCountry ? 4 : 10}
            isEmbed={isEmbed}
            canViewByCountry={canViewByCountry}
          >
            {showByCountry ? (
              <CountrySearch
                options={options}
                {...otherCountrySearchOverideProps}
              />
            ) : (
              <SiteSearch
                searchData={searchData}
                setSearchData={setSearchData}
                hasSearched={hasSearched}
                initialRadius={searchData.radius}
                initialLocation={initialLocation}
                showAllSites={showAllSites}
                setHasSearched={setHasSearched}
                useBrowserLocation={useBrowserLocation}
                {...otherSiteSearchOverideProps}
              />
            )}
          </SearchLayout>
          <SearchDetailLayout isEmbed={isEmbed}>
            {hasSearched && !showByCountry && (
              <Grid item xs={12}>
                <Grid container justify="space-between" alignItems="center">
                  <Grid item>
                    {
                      <CountHead
                        headingText={headingText}
                        count={siteCount}
                        countPostText={resultsCountText}
                      />
                    }
                  </Grid>
                  <Grid item>
                    <Link
                      href="#"
                      variant="caption"
                      color="primary"
                      underline="always"
                      onClick={handleViewModeChange}
                    >
                      {viewModeTextMapper[nextViewMode]}
                    </Link>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {showByCountry ? (
              <CountryDetails
                {...CountryDetailsOverrideProps}
                onSiteConnectClick={onSiteConnectClick}
                distanceUnit={SearchDetailsOverrideProps.unitText}
                google_place_api_key = {google_place_api_key}
              />
            ) : (
              <SiteSearchDetails
                searchData={searchData}
                hasSearched={hasSearched}
                google_place_api_key = {google_place_api_key}
                onListItemXsClick={handleListModeListItemClick}
                onSiteConnectClick={onSiteConnectClick}
                onTrialSitesChange={onTrialSitesChange}
                isMapMode={viewMode === "map"}
                isListMode={viewMode === "list"}
                {...otherSearchDetailsOverrideProps}
              />
            )}
          </SearchDetailLayout>
        </div>
      </>
      {showReferralForm && (
        <ReferralForm
          recap_key={recap_key}
          website_language={website_language}
          website_country={website_country}
          referralSiteDetails={referralSiteDetails}
          setShowReferralForm={handleReferralFormCancel}
          referral_comment_box_setting={referral_comment_box_setting}
        />
      )}
    </>
  );
};
function mapStateToProps({ locale, api, trial, sites, theme_config }) {
  let initialLocation = null;

  {
    const lat = getValue(trial, "user_location.geo_lat", null);
    const lng = getValue(trial, "user_location.geo_lng", null);
    const country = getValue(trial, "user_location.country_code", null);
    const radius = getValue(trial, "user_location.radius", defaultValue);
    const place = getValue(trial, "user_location.place", null);

    if (lat && lng && country && radius) {
      initialLocation = {
        lat,
        lng,
        country,
        radius,
        place,
      };
    }
  }

  const showAllSites = !!getValue(trial, "showAllSites", false);

  return {
    hasSearched: sites.showResults,
    headingMessage: locale.find_location_message,
    options: trial.country_list,
    siteCount: sites.totalCount,
    isEmbed: theme_config.isEmbed == "true" ? true : false,
    initialLocation,
    showAllSites,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    resetCountrySearch: () => dispatch(resetCountrySiteSearch()),
    resetSiteSearch: () => dispatch(resetSiteSearch()),
    setHasSearched: (show) => dispatch(showResults(show)),
  };
}

export default withRedux(
  connect(mapStateToProps, mapDispatchToProps)(FindSite)
);
